/**
 * Build-placement feedback: the tile lattice, coloured by whether a tower can
 * stand there.
 *
 * ## Why this exists
 *
 * Placement used to fail silently. You clicked, and either a tower appeared or
 * a toast told you it had not. The corridor, the margin around it and the
 * footprint of every tower already built are all invisible until you have
 * already spent the click on them — and on a phone, where the first thing you
 * see of a tap is its result, that is not a rule anyone can learn.
 *
 * So: while a tower is armed the whole playfield is overlaid with its own
 * placement mask. Buildable tiles draw a green lattice, blocked ground carries
 * a red wash, and the tile under the cursor is drawn hot in whichever of the
 * two applies. `main.ts` folds affordability into the same signal, because
 * "you cannot afford this" and "you cannot build here" are the same answer to
 * the same question and deserve the same colour.
 *
 * ## Why it is a shader and not four hundred DOM squares
 *
 * The frame is HDR and the whole cabinet is behind a bloom chain. Flat DOM
 * colour laid over the tube reads as a sticker: it does not glow, it does not
 * take the CRT mask, and it sits above the scanlines instead of under them.
 * The cursor cell here is written at ~3.4 emissive, well past
 * `BLOOM.threshold`, so it blooms like everything else on the playfield; the
 * field lattice is written at ~0.6, deliberately *under* the knee foot, so a
 * screen full of tile outlines tints without feeding the pyramid and washing
 * the black floor the rest of the build spent two rounds protecting.
 *
 * One draw call, one 40×22 mask texture, no per-frame allocation.
 */

import * as THREE from 'three';

import { VIEW } from '@/core/config';
import { ROLE, emissive } from '@/core/palette';
import type { RenderSystem, World } from '@/core/types';

/**
 * Green and red, from the palette rather than from a hex literal.
 *
 * `ROLE.towerBlocked` is already the red the tower preview uses, so blocked
 * ground and a blocked ghost agree by construction. There is no `towerValid`
 * role yet — `uiWave` is the lime the palette carries and the only green above
 * the bloom threshold — and adding one belongs to whoever owns `core/palette.ts`.
 * See the report.
 */
const OK_ROLE = ROLE.uiWave;
const NO_ROLE = ROLE.towerBlocked;

/** Frames the overlay takes to reach full strength. Stepped, so captures repeat. */
const FADE_STEP = 0.25;

const VERT = /* glsl */`
  varying vec2 vPos;
  void main() {
    // The geometry is pre-translated to the buffer origin, so local space and
    // buffer space are the same thing and the fragment shader can do tile
    // arithmetic without a single uniform.
    vPos = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */`
  precision highp float;

  uniform sampler2D uMask;
  uniform vec2 uTiles;
  uniform float uTileSize;
  uniform float uFade;
  uniform float uHasCursor;
  uniform vec2 uCursor;
  uniform float uFootprint;
  uniform float uValid;
  uniform vec3 uOk;
  uniform vec3 uNo;

  varying vec2 vPos;

  void main() {
    if (uFade <= 0.004) discard;

    vec2 tile = floor(vPos / uTileSize);
    float ok = texture2D(uMask, (tile + 0.5) / uTiles).r;

    // Distance to the nearest tile edge on each axis, in buffer pixels. Taken
    // from a fract of the tile coordinate rather than from a modulo, so the
    // marks cannot drift off the 16px grid the whole build snaps to.
    vec2 f = fract(vPos / uTileSize) * uTileSize;
    float ex = min(f.x, uTileSize - f.x);
    float ey = min(f.y, uTileSize - f.y);
    float thin = 1.0 - smoothstep(0.0, 1.0, min(ex, ey));
    // Corner ticks, not full lines. A closed lattice over all 880 tiles is a
    // second grid on top of the one the background already draws, and the two
    // of them together bury the corridor the player is trying to read. Four
    // 3px marks per cell say "this is a tile" and leave the frame alone.
    float tick = thin * step(ex, 3.5) * step(ey, 3.5);

    // Falls off away from where the player is actually aiming. The far field
    // keeps a whisper of the answer — enough to see the shape of the buildable
    // ground the moment a tower is armed — and the near field carries the read.
    float near = 1.0 - smoothstep(56.0, 200.0, distance(vPos, uCursor));
    float weight = mix(0.34, 1.0, near * uHasCursor);

    // Buildable ground gets ticks; blocked ground gets a wash, so the corridor
    // reads as a solid no rather than as a differently-coloured yes.
    vec3 col = uOk * tick * 0.55 * ok * weight;
    col += uNo * (1.0 - ok) * (0.05 + tick * 0.28) * weight;

    if (uHasCursor > 0.5) {
      vec3 cur = mix(uNo, uOk, uValid);
      // Square SDF around the footprint: negative inside, zero on the border.
      vec2 q = abs(vPos - uCursor) - vec2(uFootprint);
      float sdf = min(max(q.x, q.y), 0.0) + length(max(q, 0.0));
      float fill = 1.0 - smoothstep(-1.0, 0.0, sdf);
      float border = 1.0 - smoothstep(0.0, 1.4, abs(sdf));
      // A second square one tile out, so the answer still reads when the tower
      // sprite itself is sitting on top of the first one.
      vec2 q2 = abs(vPos - uCursor) - vec2(uFootprint + uTileSize);
      float sdf2 = min(max(q2.x, q2.y), 0.0) + length(max(q2, 0.0));
      float halo = 1.0 - smoothstep(0.0, 1.2, abs(sdf2));
      col += cur * (fill * 0.26 + border * 1.60 + halo * 0.55);
    }

    col *= uFade;
    if (max(col.r, max(col.g, col.b)) < 0.004) discard;
    gl_FragColor = vec4(col, 1.0);
  }
`;

/** What the overlay is being asked to draw. Mutated in place by `main.ts`. */
export interface PlacementState {
  /** A tower is armed. Drives the fade in both directions. */
  armed: boolean;
  /** True while the cursor is over the playfield and has a tile to mark. */
  hasCursor: boolean;
  /** Snapped tile centre, buffer pixels. */
  cursorX: number;
  cursorY: number;
  /** Legal *and* affordable. The one bit the cursor cell colours itself by. */
  valid: boolean;
  /** Half-edge of the marked square, buffer pixels. */
  footprint: number;
}

export class PlacementOverlay implements RenderSystem {
  readonly name = 'placementOverlay';
  readonly object: THREE.Mesh;

  readonly state: PlacementState = {
    armed: false, hasCursor: false, cursorX: 0, cursorY: 0, valid: true, footprint: 8,
  };

  private readonly maskData = new Uint8Array(VIEW.cols * VIEW.rows);
  private readonly mask: THREE.DataTexture;
  private readonly geometry: THREE.PlaneGeometry;
  private readonly material: THREE.ShaderMaterial;
  private fade = 0;

  private readonly uniforms = {
    uMask: { value: null as THREE.DataTexture | null },
    uTiles: { value: new THREE.Vector2(VIEW.cols, VIEW.rows) },
    uTileSize: { value: VIEW.tile },
    uFade: { value: 0 },
    uHasCursor: { value: 0 },
    uCursor: { value: new THREE.Vector2() },
    uFootprint: { value: 8 },
    uValid: { value: 1 },
    uOk: { value: new THREE.Vector3() },
    uNo: { value: new THREE.Vector3() },
  };

  constructor() {
    this.mask = new THREE.DataTexture(
      this.maskData, VIEW.cols, VIEW.rows, THREE.RedFormat, THREE.UnsignedByteType,
    );
    this.mask.magFilter = THREE.NearestFilter;
    this.mask.minFilter = THREE.NearestFilter;
    this.mask.wrapS = THREE.ClampToEdgeWrapping;
    this.mask.wrapT = THREE.ClampToEdgeWrapping;
    this.mask.generateMipmaps = false;
    this.mask.needsUpdate = true;
    this.uniforms.uMask.value = this.mask;

    const ok = emissive(OK_ROLE, 1);
    const no = emissive(NO_ROLE, 1);
    this.uniforms.uOk.value.set(ok[0], ok[1], ok[2]);
    this.uniforms.uNo.value.set(no[0], no[1], no[2]);

    this.geometry = new THREE.PlaneGeometry(VIEW.width, VIEW.height);
    this.geometry.translate(VIEW.width / 2, VIEW.height / 2, 0);

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });

    this.object = new THREE.Mesh(this.geometry, this.material);
    this.object.frustumCulled = false;
    // Above the corridor ribbon, below towers and enemies: the overlay is
    // ground marking, and a body walking over it has to stay readable.
    this.object.renderOrder = -50;
    this.object.visible = false;
  }

  /**
   * Uploads the combined placement mask. One byte per tile, non-zero buildable.
   *
   * `main.ts` owns the combination — `world.buildable` intersected with the
   * footprints of every tower already standing — because the second half of
   * that needs the armed archetype's own radius, which is game data this file
   * is not allowed to import.
   */
  setMask(mask: Uint8Array): void {
    const n = Math.min(mask.length, this.maskData.length);
    for (let i = 0; i < n; i++) this.maskData[i] = mask[i] !== 0 ? 255 : 0;
    this.mask.needsUpdate = true;
  }

  sync(_world: World, _alpha: number): void {
    const target = this.state.armed ? 1 : 0;
    // A stepped fade rather than a time-based one: the capture harness drives
    // whole frames, so the overlay is at the same strength on frame N of every
    // run. Four frames up, four down — enough to stop it snapping on, short
    // enough that arming still feels instant.
    if (this.fade < target) this.fade = Math.min(target, this.fade + FADE_STEP);
    else if (this.fade > target) this.fade = Math.max(target, this.fade - FADE_STEP);

    this.object.visible = this.fade > 0.004;
    if (!this.object.visible) return;

    const s = this.state;
    this.uniforms.uFade.value = this.fade;
    this.uniforms.uHasCursor.value = s.hasCursor ? 1 : 0;
    this.uniforms.uCursor.value.set(Math.round(s.cursorX), Math.round(s.cursorY));
    this.uniforms.uFootprint.value = Math.max(4, s.footprint);
    this.uniforms.uValid.value = s.valid ? 1 : 0;
  }

  dispose(): void {
    this.mask.dispose();
    this.geometry.dispose();
    this.material.dispose();
  }
}
